import React from 'react';
import { MapPin, Truck, Phone, ShieldCheck, Clock } from 'lucide-react';
import { business } from '../data/siteData';
import { PageHeader } from './About';

const calgaryHighlights = [
  'Downtown Calgary',
  'NE / NW / SE / SW Calgary',
  'Airdrie',
  'Cochrane',
  'Chestermere',
  'Okotoks',
  'High River',
  'Strathmore',
];

export default function ServiceArea() {
  return (
    <div>
      <PageHeader
        title="Service Area"
        subtitle="Proudly serving Calgary and the surrounding communities across Alberta."
        crumb="Service Area"
      />

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <div className="text-sm font-bold tracking-[0.25em] text-amber-600 uppercase mb-3">
              Where We Work
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight">
              Based in Calgary. Trusted across Alberta.
            </h2>
            <div className="w-16 h-1 bg-amber-500 my-6" />
            <p className="text-slate-600 leading-relaxed">
              Since 1988, MW Stewart Construction has been Calgary’s trusted family-run
              paving contractor. From single residential driveways to large commercial lots and
              municipal road work, our mobile crews and modern equipment let us deliver
              high-quality asphalt work right across Alberta.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="p-5 rounded-lg bg-slate-50 border border-slate-200">
                <Truck className="text-amber-600" />
                <div className="font-bold text-slate-900 mt-2">Mobile Crews</div>
                <p className="text-sm text-slate-600 mt-1">
                  We bring the equipment to your jobsite — urban or rural.
                </p>
              </div>
              <div className="p-5 rounded-lg bg-slate-50 border border-slate-200">
                <MapPin className="text-amber-600" />
                <div className="font-bold text-slate-900 mt-2">Local Knowledge</div>
                <p className="text-sm text-slate-600 mt-1">
                  Mixes & methods tuned for the Alberta climate.
                </p>
              </div>
              <div className="p-5 rounded-lg bg-slate-50 border border-slate-200">
                <ShieldCheck className="text-amber-600" />
                <div className="font-bold text-slate-900 mt-2">Licensed & Insured</div>
                <p className="text-sm text-slate-600 mt-1">
                  Fully covered for commercial and residential projects.
                </p>
              </div>
              <div className="p-5 rounded-lg bg-slate-50 border border-slate-200">
                <Clock className="text-amber-600" />
                <div className="font-bold text-slate-900 mt-2">On Schedule</div>
                <p className="text-sm text-slate-600 mt-1">
                  We finish on time so you can get back to business.
                </p>
              </div>
            </div>
            <a
              href={`tel:${business.phoneRaw}`}
              className="mt-8 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold px-6 py-3 rounded-md shadow-md transition-colors"
            >
              <Phone size={16} /> Call {business.phone}
            </a>
          </div>

          <div className="relative">
            <div className="img-zoom rounded-xl overflow-hidden shadow-2xl">
              <img
                src="/images/project-5.jpg"
                alt="Calgary paving project"
                className="w-full h-[480px] object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-slate-900 text-white px-5 py-4 rounded-xl shadow-2xl border-l-4 border-amber-500">
              <div className="text-amber-400 text-2xl font-black leading-none">YYC</div>
              <div className="text-sm font-semibold">Based in Calgary, AB</div>
            </div>
          </div>
        </div>

        {/* Calgary card */}
        <div className="bg-white rounded-xl border border-slate-200 hover:border-amber-500 hover:shadow-xl transition-all p-7 md:p-10">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-md bg-amber-500 text-slate-900 flex items-center justify-center">
              <MapPin size={22} />
            </div>
            <div>
              <div className="text-xs font-bold tracking-widest uppercase text-amber-600">
                Primary Service Area
              </div>
              <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900">
                Calgary, Alberta
              </h3>
            </div>
          </div>
          <p className="text-slate-600 leading-relaxed">
            Our crews work across Calgary and the surrounding communities every season. If
            you’re located within the Calgary region or anywhere in Southern Alberta, we can
            help.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {calgaryHighlights.map((c) => (
              <span
                key={c}
                className="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-amber-100 text-slate-700 text-sm font-medium border border-slate-200 transition-colors"
              >
                {c}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-12 bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
          <h3 className="text-xl md:text-2xl font-extrabold text-slate-900">
            Not sure if you’re in our service area?
          </h3>
          <p className="text-slate-600 mt-2">
            We regularly travel for larger projects across Alberta. Give us a call — we very
            likely cover your area.
          </p>
          <a
            href={`tel:${business.phoneRaw}`}
            className="mt-5 inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold px-6 py-3 rounded-md transition-colors"
          >
            <Phone size={16} /> {business.phone}
          </a>
        </div>
      </section>
    </div>
  );
}
